/**
 * VersionSelector -- Global Content Studio version context picker
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * Uses shadcn Combobox (Popover + Command) for the dropdown.
 * Fetches versions and auto-selects Draft (preferred) or Active on mount.
 * Syncs selection to versionStore so every Content Studio page knows
 * which version is active context.
 *
 * Shows an "Edit" badge when a Draft version is selected and
 * "Read-only" when an Active version is selected.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useVersionStore } from "../../stores/versionStore";
import { apiClient } from "../../services/apiClient";
import { Combobox } from "../Combobox";
import type { Version } from "../../types";

export function VersionSelector() {
  const selectedVersion = useVersionStore((s) => s.selectedVersion);
  const setSelectedVersion = useVersionStore((s) => s.setSelectedVersion);
  const refreshKey = useVersionStore((s) => s.refreshKey);
  const [versions, setVersions] = useState<Version[]>([]);

  // Fetch versions and auto-select in one effect (single state update)
  // Re-runs when refreshKey changes (e.g. after creating a draft)
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ data: Version[]; pagination: unknown }>("/api/versions?page_size=100")
      .then((res) => {
        if (cancelled) return;
        const selectable = res.data.filter((v) => v.state === "Draft" || v.state === "Active");
        setVersions(selectable);
        // Sync selectedVersion with fresh API data
        const current = useVersionStore.getState().selectedVersion;
        if (current) {
          const fresh = selectable.find((v) => v.id === current.id);
          if (fresh) {
            // Update with fresh data (picks up state changes like Draft→Active)
            setSelectedVersion(fresh);
          } else {
            // Current version no longer selectable (archived/deleted) — auto-select
            const draft = selectable.find((v) => v.state === "Draft");
            const active = selectable.find((v) => v.state === "Active");
            setSelectedVersion(draft ?? active ?? selectable[0] ?? null);
          }
        } else {
          // No selection yet — auto-select Draft > Active > first
          const draft = selectable.find((v) => v.state === "Draft");
          const active = selectable.find((v) => v.state === "Active");
          setSelectedVersion(draft ?? active ?? selectable[0] ?? null);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [setSelectedVersion, refreshKey]);

  const handleChange = useCallback(
    (value: string) => {
      // Prevent deselection — always keep one version selected
      if (!value) return;
      const version = versions.find((v) => v.id === value) ?? null;
      if (version) setSelectedVersion(version);
    },
    [versions, setSelectedVersion]
  );

  if (versions.length === 0) return null;

  const comboboxOptions = versions.map((v) => ({
    value: v.id,
    label: `${v.version_name} (${v.state})`,
  }));

  const isDraft = selectedVersion?.state === "Draft";

  return (
    <div className="flex items-center gap-3">
      <Combobox
        options={comboboxOptions}
        value={selectedVersion?.id ?? ""}
        onChange={handleChange}
        placeholder="Select version..."
        searchPlaceholder="Search versions..."
        emptyMessage="No versions found."
        className="h-9 min-w-45 text-sm"
      />
      {selectedVersion && (
        <span className="inline-flex items-center gap-1.5 text-sm text-slate-500 whitespace-nowrap">
          <span className={`w-2 h-2 rounded-full ${isDraft ? "bg-brand-active" : "bg-green-600"}`} />
          {isDraft ? "Editing: " : "Viewing: "}
          <span className="font-medium text-slate-700">{selectedVersion.version_name}</span>
        </span>
      )}
    </div>
  );
}

export default VersionSelector;
